/** Cart + Checkout + OrderSuccess + Service Booking pages. */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { Trash2, ShoppingBag, ArrowRight, Calendar, Check } from "lucide-react";
import {
  Container, Section, Eyebrow, PageHeader, Button, Card, CardBody, Input, Textarea, FormField,
  EmptyState, PriceBlock, Alert, ArtCard, AccentItalic, useToast, LoadingState,
} from "../../components/ui";
import { useCart } from "./CartContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useAuth } from "../auth/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";

export function CartPage() {
  const { items, update, remove, subtotal, count } = useCart();
  const { format } = useCurrency();
  return (
    <Section padded="sm" data-testid="cart-page">
      <Container>
        <PageHeader eyebrow="Cart" title="Your cart" lede={count === 0 ? "Your cart is empty." : `${count} item${count === 1 ? "" : "s"} ready for checkout.`} />
        {items.length === 0 ? (
          <EmptyState icon={ShoppingBag} title="Nothing in cart yet" description="Browse courses, sadhanas, products or services to begin." action={<Button asChild><Link to="/courses">Browse courses</Link></Button>} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
            <div className="space-y-4" data-testid="cart-items">
              {items.map((it, idx) => (
                <Card key={`${it.kind}-${it.item_id}`} variant="elevated">
                  <CardBody className="flex items-center gap-5 p-5">
                    <div className="w-20 h-20 rounded-xl overflow-hidden relative flex-shrink-0"><ArtCard seed={idx % 5} motifSeed={(idx + 1) % 4} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim mb-1">{it.kind}</div>
                      <h3 className="font-display text-[18px] tracking-tight truncate">{it.title}</h3>
                      <div className="mt-2 flex items-center gap-3">
                        <input type="number" min="1" value={it.quantity} onChange={(e) => update(it.kind, it.item_id, parseInt(e.target.value, 10) || 1)} data-testid={`cart-qty-${it.slug}`} className="w-16 bg-surface border border-line-2 rounded-lg px-2 py-1 text-[13px] focus:outline-none focus:border-mauve" />
                        <button onClick={() => remove(it.kind, it.item_id)} data-testid={`cart-remove-${it.slug}`} className="text-text-faint hover:text-red-400 transition-colors inline-flex items-center gap-1 text-[12.5px]"><Trash2 size={13} /> Remove</button>
                      </div>
                    </div>
                    <div className="text-right"><PriceBlock price={it.price_inr * it.quantity} size="sm" /></div>
                  </CardBody>
                </Card>
              ))}
            </div>
            <Card variant="elevated" className="self-start sticky top-32">
              <CardBody className="p-7">
                <Eyebrow>Order summary</Eyebrow>
                <dl className="mt-5 space-y-2.5 text-[14px]">
                  <div className="flex justify-between"><dt className="text-text-dim">Subtotal</dt><dd>{format(subtotal)}</dd></div>
                  <div className="flex justify-between"><dt className="text-text-dim">Shipping</dt><dd className="text-sage">Calculated at checkout</dd></div>
                </dl>
                <div className="flex justify-between mt-5 pt-5 border-t border-line text-[16px]"><b className="font-display">Total</b><PriceBlock price={subtotal} /></div>
                <Button asChild className="w-full mt-6" data-testid="checkout-cta"><Link to="/checkout">Proceed to checkout <ArrowRight size={14} /></Link></Button>
              </CardBody>
            </Card>
          </div>
        )}
      </Container>
    </Section>
  );
}

export function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { format } = useCurrency();
  const nav = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone,
        items: items.map((i) => ({ kind: i.kind, item_id: i.item_id, quantity: i.quantity })),
        shipping_address: form.address ? { line1: form.address } : null,
        notes: form.notes,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      toast({ variant: "success", title: "Order confirmed!", description: data.number });
      nav(`/order-success/${data.id}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (items.length === 0) return (
    <Section padded="sm"><Container>
      <EmptyState icon={ShoppingBag} title="Your cart is empty" description="Add items before checking out." action={<Button asChild><Link to="/courses">Browse</Link></Button>} />
    </Container></Section>
  );

  return (
    <Section padded="sm" data-testid="checkout-page">
      <Container>
        <PageHeader eyebrow="Checkout" title={<>Complete your <AccentItalic>study</AccentItalic></>} lede="Review your order and confirm your details. Payment is processed securely." />
        {err && <Alert variant="error" className="mb-6">{err}</Alert>}
        <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
          <Card variant="elevated"><CardBody className="p-7 space-y-5">
            <Eyebrow>Contact</Eyebrow>
            <FormField id="ch-name" label="Full name" required><Input id="ch-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" /></FormField>
            <FormField id="ch-email" label="Email" required><Input id="ch-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" /></FormField>
            <FormField id="ch-phone" label="Phone (optional)"><Input id="ch-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" /></FormField>
            {items.some(i => i.kind === "product") && (
              <FormField id="ch-addr" label="Shipping address" required><Textarea id="ch-addr" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" /></FormField>
            )}
            <FormField id="ch-notes" label="Order notes (optional)"><Textarea id="ch-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormField>
            <Alert variant="info">Payment integration will be activated in a future phase. For now, orders confirm immediately so the end-to-end flow can be experienced.</Alert>
          </CardBody></Card>
          <Card variant="elevated" className="self-start"><CardBody className="p-7">
            <Eyebrow>Order summary</Eyebrow>
            <ul className="mt-5 space-y-3" data-testid="checkout-items">
              {items.map((it) => (
                <li key={`${it.kind}-${it.item_id}`} className="flex justify-between gap-3 text-[14px]">
                  <span className="min-w-0"><b className="block truncate">{it.title}</b><span className="text-[11.5px] text-text-faint font-mono uppercase tracking-[0.14em]">{it.kind} · ×{it.quantity}</span></span>
                  <span>{format(it.price_inr * it.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between mt-6 pt-5 border-t border-line text-[16px]"><b className="font-display">Total</b><PriceBlock price={subtotal} /></div>
            <Button type="submit" className="w-full mt-6" disabled={submitting} data-testid="checkout-submit">{submitting ? "Placing order…" : "Place order"} <ArrowRight size={14} /></Button>
          </CardBody></Card>
        </form>
      </Container>
    </Section>
  );
}

export function OrderSuccessPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => { if (id) api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {}); }, [id]);
  return (
    <Section padded="sm" data-testid="order-success-page">
      <Container>
        <div className="max-w-[640px] mx-auto text-center pt-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sage/20 border border-sage/40 mb-6"><Check size={24} className="text-sage" /></div>
          <Eyebrow centered>Order confirmed</Eyebrow>
          <h1 className="font-display text-[clamp(36px,5vw,56px)] tracking-[-0.01em] mt-5 leading-tight">Thank you, <AccentItalic>seeker</AccentItalic>.</h1>
          <p className="lede mt-5 mx-auto">Your order has been placed. {order && <>Your reference number is <b className="text-text font-mono">{order.number}</b>.</>}</p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Button asChild><Link to="/dashboard/orders">View my orders</Link></Button>
            <Button asChild variant="ghost"><Link to="/">Continue browsing</Link></Button>
          </div>
        </div>
      </Container>
    </Section>
  );
}

export function BookingPage() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "10:00", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const { user } = useAuth();
  const { format } = useCurrency();
  useEffect(() => { api.get(`/services/${slug}`).then((r) => { setSvc(r.data); if (user) setForm(f => ({ ...f, name: user.name || "", email: user.email || "" })); }); }, [slug, user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const scheduled_for = new Date(`${form.date}T${form.time}:00`).toISOString();
      const { data } = await api.post("/bookings", {
        service_id: svc.id, name: form.name, email: form.email, phone: form.phone,
        scheduled_for, notes: form.notes,
      });
      setDone(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  if (!svc) return <Section padded="sm"><Container><LoadingState /></Container></Section>;
  if (done) return (
    <Section padded="sm"><Container>
      <div className="max-w-[640px] mx-auto text-center pt-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sage/20 border border-sage/40 mb-6"><Calendar size={24} className="text-sage" /></div>
        <Eyebrow centered>Booking received</Eyebrow>
        <h1 className="font-display text-[clamp(36px,5vw,56px)] tracking-[-0.01em] mt-5 leading-tight">We'll be in <AccentItalic>touch</AccentItalic>.</h1>
        <p className="lede mt-5 mx-auto">Your booking for <b>{svc.title}</b> on {new Date(form.date + 'T' + form.time).toLocaleString()} has been received. We'll confirm by email shortly.</p>
        <div className="mt-8"><Button asChild><Link to="/dashboard/bookings">View bookings</Link></Button></div>
      </div>
    </Container></Section>
  );

  return (
    <Section padded="sm" data-testid="booking-page">
      <Container>
        <PageHeader eyebrow={`Book · ${svc.title}`} title="Schedule your session" lede={`${svc.summary} · ${svc.duration_minutes} minutes`} />
        {err && <Alert variant="error" className="mb-6">{err}</Alert>}
        <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
          <Card variant="elevated"><CardBody className="p-7 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="b-name" label="Name" required><Input id="b-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" /></FormField>
              <FormField id="b-email" label="Email" required><Input id="b-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" /></FormField>
            </div>
            <FormField id="b-phone" label="Phone (optional)"><Input id="b-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField id="b-date" label="Date" required><Input id="b-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="booking-date" /></FormField>
              <FormField id="b-time" label="Time" required><Input id="b-time" type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="booking-time" /></FormField>
            </div>
            <FormField id="b-notes" label="Notes (optional)"><Textarea id="b-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormField>
          </CardBody></Card>
          <Card variant="elevated" className="self-start"><CardBody className="p-7">
            <Eyebrow>Service</Eyebrow>
            <h3 className="font-display text-[22px] mt-3 leading-tight">{svc.title}</h3>
            <p className="text-text-dim text-[13.5px] mt-3">{svc.summary}</p>
            <div className="mt-5 flex justify-between items-center pt-5 border-t border-line"><span className="text-text-dim text-[14px]">Fee</span><PriceBlock price={svc.price_inr} size="sm" /></div>
            <Button type="submit" className="w-full mt-6" disabled={submitting} data-testid="booking-submit">{submitting ? "Booking…" : "Confirm booking"} <Calendar size={14} /></Button>
          </CardBody></Card>
        </form>
      </Container>
    </Section>
  );
}
